import React from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, View } from 'react-native'

import Text from '@common/components/Text'
import TokenIcon from '@common/components/TokenIcon'
import useTheme from '@common/hooks/useTheme'
import { RADII } from '@common/modules/dashboard/home-kit/tokens'
import { THEME_TYPES } from '@common/styles/themeConfig'
import { hexToRgba } from '@common/styles/utils/common'
import flexbox from '@common/styles/utils/flexbox'
import { DcaOrderStatus, DcaToken } from '@web/extension-services/background/controllers/dca/types'
import { DCA_CHAIN_ID } from '@web/modules/swap-and-bridge/hooks/useDca'

import { getOrderStatusText } from './historyText'

export const shortAddress = (address: string) => `${address.slice(0, 6)}…${address.slice(-4)}`

/** Symbol, or the shortened address when the token is unknown. */
export const tokenLabel = (token: DcaToken | null, address: string) =>
  token?.symbol || shortAddress(address)

/**
 * `CANCELLING` is local: the cancel transactions are being signed or mined.
 * The backend only reports CANCELLED once the indexer has seen them.
 */
export type DcaDisplayStatus = DcaOrderStatus | 'CANCELLING'

const useStatusColors = (status: DcaDisplayStatus) => {
  const { theme } = useTheme()
  switch (status) {
    case 'CANCELLING':
      return { text: theme.warningText, background: theme.warningBackground }
    case 'ACTIVE':
      return { text: theme.successText, background: theme.successBackground }
    case 'PAUSED':
      return { text: theme.warningText, background: theme.warningBackground }
    case 'COMPLETED':
      return { text: theme.infoText, background: theme.infoBackground }
    case 'CANCELLED':
      return { text: theme.errorText, background: theme.errorBackground }
    default:
      return { text: theme.secondaryText, background: theme.secondaryBackground }
  }
}

export const DcaStatusPill = React.memo(({ status }: { status: DcaDisplayStatus }) => {
  const { t } = useTranslation()
  const colors = useStatusColors(status)

  return (
    <View
      style={[
        flexbox.directionRow,
        flexbox.alignCenter,
        {
          gap: 6,
          height: 26,
          paddingHorizontal: 10,
          borderRadius: RADII.pill,
          backgroundColor: colors.background as string,
          alignSelf: 'flex-start'
        }
      ]}
      testID={`dca-status-${status}`}
    >
      <View
        style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: colors.text as string }}
      />
      <Text fontSize={12} weight="medium" color={colors.text as string}>
        {status === 'CANCELLING' ? t('Cancelling') : getOrderStatusText(status, t)}
      </Text>
    </View>
  )
})

/** Both tokens of an order, each with the PulseChain badge, then "IN → OUT". */
export const DcaPair = React.memo(
  ({
    tokenIn,
    tokenOut,
    tokenInAddress,
    tokenOutAddress,
    size = 28,
    fontSize = 14,
    vertical
  }: {
    tokenIn: DcaToken | null
    tokenOut: DcaToken | null
    tokenInAddress: string
    tokenOutAddress: string
    size?: number
    fontSize?: number
    /** Icons above the label (Cancel modal) instead of beside it. */
    vertical?: boolean
  }) => (
    <View
      style={[
        vertical ? flexbox.alignCenter : [flexbox.directionRow, flexbox.alignCenter],
        { gap: 10, flexShrink: 1, minWidth: 0 }
      ]}
    >
      <View style={[flexbox.directionRow, { gap: 4 }]}>
        {[
          { token: tokenIn, address: tokenInAddress },
          { token: tokenOut, address: tokenOutAddress }
        ].map(({ token, address }) => (
          <TokenIcon
            key={address}
            width={size}
            height={size}
            // DCA is PulseChain-only: the chain badge adds nothing.
            withNetworkIcon={false}
            address={address}
            chainId={BigInt(DCA_CHAIN_ID)}
            uri={token?.logoUri || undefined}
          />
        ))}
      </View>
      <Text fontSize={fontSize} weight="semiBold" numberOfLines={1} style={{ flexShrink: 1 }}>
        {`${tokenLabel(tokenIn, tokenInAddress)} → ${tokenLabel(tokenOut, tokenOutAddress)}`}
      </Text>
    </View>
  )
)

export const DcaProgressBar = React.memo(({ percent }: { percent: number }) => {
  const { theme } = useTheme()
  return (
    <View
      style={{
        height: 8,
        borderRadius: 4,
        overflow: 'hidden',
        backgroundColor: theme.tertiaryBackground as string
      }}
    >
      <View
        style={{
          width: `${percent}%`,
          height: '100%',
          borderRadius: 4,
          backgroundColor: theme.successDecorative as string
        }}
      />
    </View>
  )
})

/** Underlined, equal-width tabs (All / Active / Past, Overview / Executions). */
export const DcaTabs = <T extends string>({
  tabs,
  value,
  onChange,
  testIDPrefix
}: {
  tabs: { value: T; label: string }[]
  value: T
  onChange: (value: T) => void
  testIDPrefix: string
}) => {
  const { theme, themeType } = useTheme()
  // Same hairline as the rows under it (DcaRow); secondaryBorder read as a heavy rule.
  const dividerBorder =
    themeType === THEME_TYPES.LIGHT
      ? hexToRgba(theme.primaryBorder, 0.3)
      : (theme.primaryBorder as string)
  return (
    <View
      style={[flexbox.directionRow, { borderBottomWidth: 1, borderBottomColor: dividerBorder }]}
    >
      {tabs.map((tab) => {
        const isActive = tab.value === value
        return (
          <Pressable
            key={tab.value}
            onPress={() => onChange(tab.value)}
            testID={`${testIDPrefix}-${tab.value}`}
            // Width follows the label, spare room shared out: equal thirds cut
            // longer translations ("Vergangene DCAs") on a phone. When even that
            // does not fit ("Wszystkie DCA"), the label wraps to a second line.
            style={[
              flexbox.center,
              {
                flexGrow: 1,
                flexShrink: 1,
                flexBasis: 'auto',
                minHeight: 44,
                paddingVertical: 4,
                paddingHorizontal: 8
              }
            ]}
          >
            <Text
              fontSize={14}
              weight={isActive ? 'semiBold' : 'medium'}
              appearance={isActive ? 'primaryText' : 'secondaryText'}
              numberOfLines={2}
              style={{ textAlign: 'center' }}
            >
              {tab.label}
            </Text>
            {isActive && (
              <View
                style={{
                  position: 'absolute',
                  bottom: -1,
                  left: 12,
                  right: 12,
                  height: 2,
                  borderRadius: 1,
                  backgroundColor: theme.primaryText as string
                }}
              />
            )}
          </Pressable>
        )
      })}
    </View>
  )
}
